import React, { useState, useEffect, useCallback } from 'react';
import { mySum } from '../helpers';
import { FileInput } from './FileInput';
import TelemetryTableValidator from './TelemetryTableValidator';
import Papa from 'papaparse';
import Typography from '@mui/material/Typography';
import { Container, Box } from '@mui/material';

export default function TelemetryUploader({
  selectedRunDetails,
  telemetryFileMetaData,
  liftTelemetryFileMetaData,
  telemetryDataArray,
  liftTelemetryDataArray,
}) {
  // need to move this state up to sessionUpload if I want to be able to go back from video and retain the grid.
  const [selectedFile, setSelectedFile] = useState(null);
  const [papaParseObject, setPapaParseObject] = useState({});

  // select csv file and convert to array using papaparse
  const handleFileSelection = (file) => {
    if (file) {
      setSelectedFile(file);
      console.log(file);
      Papa.parse(file, {
        skipEmptyLines: true,
        // skipFirstNLines: 14, // not implmented in the latest NPM version
        // header: true,
        complete: function (results) {
          setPapaParseObject(results);
        },
      });
    } else {
      // clear states
      setSelectedFile(null);
      setPapaParseObject({});
      liftTelemetryDataArray([]);
      liftTelemetryFileMetaData({
        Format: '',
        Venue: '',
        Vehicle: '',
        User: '',
        Date: '',
        Time: '',
        SampleRate: 0,
        Duration: 0,
        Segment: '',
        BeaconMarkers: [],
        SegmentTimes: [],
      });
    }
  };

  // split the papaparse array into two so we can process the header and the data
  useEffect(() => {
    function parseMotecHeaderInfo() {
      const data = papaParseObject.data;
      const headerArrayPos = data.findIndex(
        (row) => row[0] === 'Time' && row[1] === 'Distance'
      );
      // split into two arrays: metaData, data
      const metaDataArray = data.slice(0, headerArrayPos);
      const motecDataArray = data.slice(headerArrayPos);
      liftTelemetryDataArray(motecDataArray);

      // process the metaData into an object
      const metaObjectDef = {
        Format: 'Format',
        Venue: 'Venue',
        Vehicle: 'Vehicle',
        User: 'User',
        Date: 'Date',
        Time: 'Time',
        SampleRate: 'Sample Rate',
        Duration: 'Duration',
        Segment: 'Segment',
        BeaconMarkers: 'Beacon Markers',
        SegmentTimes: 'Segment Times',
      };
      for (const property in metaObjectDef) {
        const foundProp = metaDataArray?.find(
          (d) => d[0] === metaObjectDef[property]
        );
        if (typeof foundProp !== 'undefined') {
          let retVal;
          if (property === 'BeaconMarkers') {
            retVal = foundProp[1].split(',').map((item) => Number(item.trim()));
          } else if (property === 'SegmentTimes') {
            retVal = foundProp.slice(1);
          } else {
            retVal = foundProp[1];
          }
          const fileDetails = {
            Filename: selectedFile.name,
            LastModified: new Date(selectedFile.lastModified).toISOString(),
            Size: selectedFile.size,
          };
          liftTelemetryFileMetaData((s) => ({
            ...s,
            [property]: retVal,
            FileDetails: fileDetails,
          }));
        }
      }
    }
    Object.keys(papaParseObject).length !== 0 &&
      selectedFile &&
      parseMotecHeaderInfo();
  }, [papaParseObject, selectedFile]);

  const memoizedLiftTelemetryFileMetaData = useCallback(
    (data) => {
      liftTelemetryFileMetaData((d) => ({ ...d, ...data }));
    },
    [liftTelemetryFileMetaData]
  );

  // calculate telemetry file laptimes by subtracting beacon markers
  useEffect(() => {
    const markersArray = telemetryFileMetaData?.BeaconMarkers;
    const newArray = [];
    // if (markersArray.length > 0) {
    if (markersArray) {
      newArray.push(markersArray[0]);
      for (let [index, marker] of markersArray.entries()) {
        if (index >= 0 && index < markersArray.length - 1) {
          const lapTime = mySum(markersArray[index + 1], -1 * marker, 3);
          newArray.push(lapTime);
        }
      }
      liftTelemetryFileMetaData((d) => ({ ...d, LapTimeData: newArray }));
      // memoizedLiftTelemetryFileMetaData({ LapTimeData: newArray });
    }
  }, [telemetryFileMetaData.BeaconMarkers]);

  // find the beginning of each new lap
  useEffect(() => {
    if (
      typeof telemetryFileMetaData.LapTimeData !== 'undefined' &&
      telemetryDataArray?.length > 0
    ) {
      const lapArrayIndexes = telemetryFileMetaData.LapTimeData.map((laptime) =>
        telemetryDataArray?.findIndex(
          (lap) => String(lap[0]) === String(laptime)
        )
      );
      liftTelemetryFileMetaData((d) => ({
        ...d,
        LapArrayIndexes: lapArrayIndexes,
      }));
    }
  }, [telemetryFileMetaData.LapTimeData, telemetryDataArray]);

  return (
    <React.Fragment>
      <Container>
        {/* {selectedFile && <pre>{selectedFile.name}</pre>} */}
        {/* {papaParseObject && <pre>{JSON.stringify(papaParseObject, null, 2)}</pre>} */}
        <Typography variant="h6" gutterBottom>
          Chose the telemetry file to upload. It must be a 'Motec CSV' file
          exported from AIM Race Studio.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            columnGap: '20px',
            paddingBottom: '20px',
          }}
        >
          <FileInput file={selectedFile} handleChange={handleFileSelection} />
        </Box>
        {selectedFile && (
          <TelemetryTableValidator
            telemetrymetadata={telemetryFileMetaData}
            liftTelemetryFileMetaData={liftTelemetryFileMetaData}
            selectedRunDetails={selectedRunDetails}
          />
        )}
      </Container>
    </React.Fragment>
  );
}
